import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import LineString from 'ol/geom/LineString';
import {fromLonLat} from 'ol/proj';

// Code to handle creation of wind direction arrows based on u and v wind components, 
// and styling them for display on the map.

// Calculate wind direction from u and v components
// u_wind: eastern wind velocity (m/s) - positive = east
// v_wind: northern wind velocity (m/s) - positive = north
// Returns: direction in degrees where 0 = north, 90 = east, 180 = south, 270 = west
export function getWindDirection(u_wind, v_wind) {
  u_wind = parseFloat(u_wind) || 0;
  v_wind = parseFloat(v_wind) || 0;
  
  // Calculate direction using atan2(u, v) for meteorological convention
  // North = 0°, East = 90°, South = 180°, West = 270°
  let direction = Math.atan2(u_wind, v_wind) * (180 / Math.PI);
  
  // Normalize to 0-360 range
  if (direction < 0) {
    direction += 360;
  }
  
  return direction;
}

export function createArrowFeature(lonLatCoord, direction, length, scale =  0.03, background_width = 1, offsetDegrees = 0.3, width = 1.5, color = '#FF0000') {
  // lonLatCoord: [longitude, latitude] in WGS84
  // direction: angle in degrees (0 = north, 90 = east, 180 = south, 270 = west)
  // length: length in degrees (approximate)
  // scale: optional scaling factor
  // offsetDegrees: offset to the right (east) from the point center (in degrees, ~0.001 ≈ 30px at typical zoom)
  
  const radians = (direction * Math.PI) / 180;
  const scaledLength = length * scale;
  
  // Calculate start and end points centered on the coordinate
  const halfLength = scaledLength / 2;
  const startLon = lonLatCoord[0] + offsetDegrees - halfLength * Math.sin(radians);
  const startLat = lonLatCoord[1] - halfLength * Math.cos(radians);
  const endLon = lonLatCoord[0] + offsetDegrees + halfLength * Math.sin(radians);
  const endLat = lonLatCoord[1] + halfLength * Math.cos(radians);
  
  // Project points to map coordinates
  const startCoord = fromLonLat([startLon, startLat]);
  const endCoord = fromLonLat([endLon, endLat]);
  const centerCoord = fromLonLat(lonLatCoord);
  
  // Create main arrow line
  const lineGeometry = new LineString([
    startCoord,
    endCoord
  ]);
  
  const arrowFeature = new Feature({
    geometry: lineGeometry,
    isArrow: true
  });
  
  // Style the arrow with the white background, then the line in red on top
  arrowFeature.setStyle([
      new Style({
        stroke: new Stroke({
          color: '#FFFFFF',
          width: width+background_width, // Must be wider than the foreground line
          lineCap: 'round',
          lineJoin: 'round'
        })
      }),
      new Style({
        stroke: new Stroke({
          color: color,
          width: width
        })
      })
    ]
  );
  
  // Create arrowhead triangle
  const arrowheadLength = scaledLength * 0.20; // Arrowhead length is 10% of arrow length
  const arrowheadAngle = 30 * Math.PI / 180; // 30 degree spread on each side
  
  // Left point of arrowhead - goes back from end point at angle - 30 degrees
  const leftAngle = radians - arrowheadAngle;
  const leftLon = endLon - arrowheadLength * Math.sin(leftAngle);
  const leftLat = endLat - arrowheadLength * Math.cos(leftAngle);
  
  // Right point of arrowhead - goes back from end point at angle + 30 degrees
  const rightAngle = radians + arrowheadAngle;
  const rightLon = endLon - arrowheadLength * Math.sin(rightAngle);
  const rightLat = endLat - arrowheadLength * Math.cos(rightAngle);
  
  // Create arrowhead geometry
  const arrowheadGeometry = new LineString([
    fromLonLat([leftLon, leftLat]),
    endCoord,
    fromLonLat([rightLon, rightLat])
  ]);
  
  const arrowheadFeature = new Feature({
    geometry: arrowheadGeometry,
    isArrow: true
  });
  
  arrowheadFeature.setStyle(new Style({
    stroke: new Stroke({
      color: color,
      width: width
    })
  }));
  
  // Return both features as an array
  return [arrowFeature, arrowheadFeature];
}
